import type React from "react";
import { isValidElement } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  List,
  ListItem,
  Chip,
  Box,
  Avatar,
} from "@mui/material";
import { CheckCircle, Star } from "@mui/icons-material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";

interface Package {
  id: number;
  name: string;
  price: number;
  contents: string[];
  package_type: string;
  recommended?: boolean;
  package_logo?: string | null;
  icon?: React.ReactNode;
}

interface PackageSelectorProps {
  packages: Package[];
  selectedPackage: string;
  onPackageSelect: (packageName: string) => void;
}

const PackageSelector: React.FC<PackageSelectorProps> = ({
  packages,
  selectedPackage,
  onPackageSelect,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  return (
    <Card sx={{ mb: 4, boxShadow: 0 }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Select Service Package (recommended)
        </Typography>
        <Grid container spacing={3}>
          {packages.map((pkg) => (
            <Grid size={{ xs: 12, md: 4 }} key={pkg.id}>
              <Card
                onClick={() => onPackageSelect(pkg.name)}
                sx={{
                  position: "relative",
                  boxShadow: selectedPackage === pkg.name ? 4 : 0,
                  border: "2px solid",
                  borderColor:
                    selectedPackage === pkg.name
                      ? reduxTheme.primary_colors[400]
                      : "divider",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backgroundColor:
                    selectedPackage === pkg.name
                      ? "action.selected" 
                      : "transparent",
                  display: "flex",
                  flexDirection: "column",   
                  alignItems: "flex-start",  
                  p: 3,                      
                  height: "100%",           
                  "&:hover": {
                    boxShadow: 4,
                    borderColor: reduxTheme.primary_colors[400],
                  },
                }}
              >
                {/* Recommended Status Badge */}
                {pkg.recommended && (
                  <Chip
                    label="Recommended"
                    color="primary"
                    size="small"
                    icon={<Star />}
                    sx={{
                      position: "absolute",
                      top: 16,
                      right: 16,
                      zIndex: 1,
                    }}
                  />
                )}

                <Avatar
                  src={
                    typeof pkg.package_logo === "string"
                      ? pkg.package_logo
                      : undefined
                  }
                  imgProps={{
                    style: {
                      objectFit: "contain",
                      padding: pkg.package_logo ? "4px" : "0px",
                    },
                  }}
                  sx={{
                    background: pkg.package_logo
                      ? "transparent"
                      : reduxTheme.primary_colors[400],
                    color: "#fff",
                    width: 50,
                    height: 50,
                    mb: 2.5,
                    flexShrink: 0,
                    boxShadow: `0 4px 12px ${reduxTheme.primary_colors[400]}33`,
                    border: pkg.package_logo 
                      ? `1px solid ${reduxTheme.primary_colors[400]}22` 
                      : "none",
                  }}
                >
                  {isValidElement(pkg.icon) ? pkg.icon : <EmojiEventsIcon />}
                </Avatar>

                {/* 2. Content & Checklist Block */}
                <Box 
                  sx={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    alignItems: "flex-start",
                    width: "100%", 
                    flexGrow: 1,
                  }}
                >
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      lineHeight: 1.3, 
                      mb: 1,
                      fontWeight: 600,
                      width: "100%",
                      overflowWrap: "break-word",
                    }}
                  >
                    {pkg.name}
                  </Typography>

                  <Typography 
                    variant="h4" 
                    color="primary" 
                    sx={{ fontWeight: "bold", mb: 2 }}
                  >
                    ${pkg.price.toFixed(2)}
                  </Typography>

                  {/* Feature Checklist aligned nicely at the bottom */}
                  <List dense sx={{ width: "100%", mt: "auto", pt: 1 }}>
                    {pkg.contents.slice(0, 5).map((feature, index) => (
                      <ListItem
                        key={index}
                        disablePadding
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <CheckCircle
                          sx={{
                            fontSize: 16,
                            mt: "4px",
                            color: "success.main",
                          }}
                        />
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            "& p": { margin: 0 },
                            "& div": { margin: 0 },
                          }}
                          dangerouslySetInnerHTML={{ __html: feature }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default PackageSelector;